/**
 * Compositions — les dispositions prêtes à poser, partagées entre l'éditeur
 * classique et l'éditeur de site.
 *
 * Une composition décrit une grille ET la nature du contenu de chaque cellule,
 * sans jamais nommer une box d'un éditeur en particulier : le vocabulaire est
 * neutre (`COMPOSITION_BOX`), chaque éditeur le traduit chez lui — le classique
 * vers ses `ItemTypes`, le site vers ses types de blocs Puck.
 *
 * Schéma, récursif :
 *
 *   colonne = { span, box }                    // feuille : une box, ou vide
 *           | { span, rows: [{ columns }] }    // pile de lignes
 *
 * Une colonne porte `box` OU `rows`, jamais les deux : c'est la contrainte des
 * deux modèles de données à la fois (une colonne du document qui a des
 * `rowContainerIds` ne porte plus de box, et une colonne de Bloc web est une
 * zone qui accueille soit une box, soit une grille imbriquée).
 *
 * `span` est en **douzièmes**, la grille du site (`GRID_COLUMNS = 12`). Le
 * classique, qui raisonne en fractions, divise par 12 : la conversion est
 * exacte pour toutes les largeurs employées ici (6, 4, 3, 8 douzièmes).
 *
 * Profondeur : deux niveaux, pas plus. Au-delà, les « Réglages avancés » du
 * panneau de droite du classique ne savent plus éditer la grille obtenue, et
 * l'utilisateur se retrouve avec une structure qu'il ne peut plus défaire.
 */

/** Nature du contenu d'une cellule. Une cellule sans `box` reste vide. */
export const COMPOSITION_BOX = {
  TEXT: 'text',
  TITLE_2: 'title2',
  TITLE_3: 'title3',
  IMAGE: 'image',
  // Le document ne sait pas rendre une vidéo : les compositions qui en posent
  // une n'existent que sur le site (cf. constants/containerTemplates.js).
  VIDEO: 'video',
};

export const GRID_SPAN = 12;

const HALF = GRID_SPAN / 2;
const THIRD = GRID_SPAN / 3;

// Raccourcis de lecture : le catalogue ci-dessous se lit comme un croquis.
const cell = (box) => (box ? { box } : {});
const stack = (...cells) => ({
  rows: cells.map((c) => ({ columns: [{ span: GRID_SPAN, ...c }] })),
});

const COMPOSITIONS = [
  {
    id: 'media-left-text-right',
    label: 'Image à gauche, texte à droite',
    columns: [
      { span: THIRD, ...cell(COMPOSITION_BOX.IMAGE) },
      { span: THIRD * 2, ...cell(COMPOSITION_BOX.TEXT) },
    ],
  },
  {
    id: 'text-left-media-right',
    label: 'Texte à gauche, image à droite',
    columns: [
      { span: THIRD * 2, ...cell(COMPOSITION_BOX.TEXT) },
      { span: THIRD, ...cell(COMPOSITION_BOX.IMAGE) },
    ],
  },
  {
    id: 'video-left-text-right',
    label: 'Vidéo à gauche, texte à droite',
    columns: [
      { span: THIRD, ...cell(COMPOSITION_BOX.VIDEO) },
      { span: THIRD * 2, ...cell(COMPOSITION_BOX.TEXT) },
    ],
  },
  {
    id: 'text-left-video-right',
    label: 'Texte à gauche, vidéo à droite',
    columns: [
      { span: THIRD * 2, ...cell(COMPOSITION_BOX.TEXT) },
      { span: THIRD, ...cell(COMPOSITION_BOX.VIDEO) },
    ],
  },
  {
    id: 'text-with-stacked-media',
    label: 'Texte + image légendée',
    columns: [
      { span: HALF, ...cell(COMPOSITION_BOX.TEXT) },
      {
        span: HALF,
        ...stack(cell(COMPOSITION_BOX.IMAGE), cell(COMPOSITION_BOX.TEXT)),
      },
    ],
  },
  {
    id: 'three-cards',
    label: 'Trois cartes illustrées',
    columns: [
      { span: THIRD, ...stack(cell(COMPOSITION_BOX.IMAGE), cell(COMPOSITION_BOX.TEXT)) },
      { span: THIRD, ...stack(cell(COMPOSITION_BOX.IMAGE), cell(COMPOSITION_BOX.TEXT)) },
      { span: THIRD, ...stack(cell(COMPOSITION_BOX.IMAGE), cell(COMPOSITION_BOX.TEXT)) },
    ],
  },
  {
    id: 'two-columns-with-headings',
    label: 'Deux colonnes titrées',
    columns: [
      { span: HALF, ...stack(cell(COMPOSITION_BOX.TITLE_3), cell(COMPOSITION_BOX.TEXT)) },
      { span: HALF, ...stack(cell(COMPOSITION_BOX.TITLE_3), cell(COMPOSITION_BOX.TEXT)) },
    ],
  },
  {
    id: 'banner-image-with-caption',
    label: 'Image pleine largeur légendée',
    columns: [
      {
        span: GRID_SPAN,
        ...stack(cell(COMPOSITION_BOX.IMAGE), cell(COMPOSITION_BOX.TEXT)),
      },
    ],
  },
  {
    id: 'title-over-two-columns',
    // Seule entrée à mettre plusieurs sous-colonnes dans une ligne : un titre
    // pleine largeur ne peut pas cohabiter avec deux colonnes de texte au même
    // niveau, il lui faut sa propre ligne.
    label: 'Titre + deux colonnes',
    columns: [
      {
        span: GRID_SPAN,
        rows: [
          { columns: [{ span: GRID_SPAN, box: COMPOSITION_BOX.TITLE_2 }] },
          {
            columns: [
              { span: HALF, box: COMPOSITION_BOX.TEXT },
              { span: HALF, box: COMPOSITION_BOX.TEXT },
            ],
          },
        ],
      },
    ],
  },
];

/** Toutes les compositions, dans l'ordre du catalogue. */
export const allCompositions = () => COMPOSITIONS;

export const getComposition = (id) => COMPOSITIONS.find((c) => c.id === id) ?? null;

/** Les compositions demandées, dans l'ordre demandé. Un id inconnu est ignoré. */
export const compositionsByIds = (ids = []) => ids.map(getComposition).filter(Boolean);
